'use client';

import { useCallback } from 'react';
import { toast } from 'sonner';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/lib/storage/web-storage';

import { apiClient } from '@/lib/api/client';
import {
  setPrimaryToken,
  setTemporaryToken,
} from '@/lib/auth/storage';
import {
  buildTemporaryEditorRedirect,
  formatFromLandingPath,
  persistLandingOrigin,
} from '@/features/resume/lib/origin-target-format';

import { type TemporarySignupAction } from '../lib/marketing-constants';
import { useMarketingUrls } from './use-marketing-urls';

type TemporarySignupResponse = {
  status: string;
  token?: string;
  message?: string;
};

function showSignupError(message: string) {
  toast.error(message);
}

export function useTemporarySignup() {
  const { webDomain } = useMarketingUrls();

  const redirectToEditor = useCallback(
    (action: TemporarySignupAction) => {
      const pathname = window.location.pathname;
      if (formatFromLandingPath(pathname)) {
        persistLandingOrigin(pathname);
      }
      window.location.href = buildTemporaryEditorRedirect(
        webDomain,
        action,
        pathname,
      );
    },
    [webDomain],
  );

  const createTemporaryUser = useCallback(
    async (
      action: TemporarySignupAction = 'import',
      templateId = '',
      buttonElement?: HTMLElement | null,
    ) => {
      const clickedButton =
        buttonElement ||
        (document.activeElement instanceof HTMLAnchorElement
          ? document.activeElement
          : null);
      const isButton = clickedButton && clickedButton.tagName === 'A';

      if (isButton) {
        clickedButton.classList.add('btn-loading');
        clickedButton.style.cursor = 'wait';
      }

      const existingTemporary = getLocalStorageItem('temporary_token');
      if (existingTemporary) {
        redirectToEditor(action);
        return;
      }

      const token = getLocalStorageItem('tokensz');
      if (token && token.length > 0) {
        redirectToEditor(action);
        return;
      }

      try {
        const body = templateId ? { template_id: templateId } : {};
        const { data } = await apiClient.post<TemporarySignupResponse>(
          '/pro/auth/temporary-signup',
          body,
          { timeout: 10000 },
        );

        if (data.status === 'success' && data.token) {
          setTemporaryToken(data.token);
          setPrimaryToken(data.token);
          redirectToEditor(action);
          return;
        }

        if (isButton) {
          clickedButton.classList.remove('btn-loading');
          clickedButton.style.cursor = 'pointer';
        }
        showSignupError('Unable to create session. Please try again.');
      } catch (error) {
        if (isButton) {
          clickedButton.classList.remove('btn-loading');
          clickedButton.style.cursor = 'pointer';
        }
        const isTimeout =
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          error.code === 'ECONNABORTED';
        showSignupError(
          isTimeout
            ? 'Request timed out. Please check your connection and try again.'
            : 'Unable to connect. Please try again.',
        );
      }
    },
    [redirectToEditor],
  );

  return { createTemporaryUser, redirectToEditor };
}
